/**
 * 看板のカードの「詳細」ボタンの行き先 — ステージ・業務フロー・業務・業務ルール・画面のカードから開く画面。
 *
 * **業務マップと案件の変更一覧が同じ行き先を使う**（引継書 §3.1）。ステージ → ステージの図、
 * 業務フロー → 業務フローの図（案件モードは案件から見た業務フロー）、業務 → 業務詳細、業務ルール →
 * 業務ルールの画面、画面 → 画面詳細。画面ごとに組むと、同じカードの「詳細」が
 * 画面によって別の場所へ飛ぶ。案件の変更一覧の横断的に関わるデータ（用語 → 用語詳細、マスター →
 * マスター詳細）は、その節ごと消した（Dhik 2026-09-29「PJ変更一覧の横断する変更の情報は丸ごと消してほしい」）。
 *
 * ボタンの見た目・見える文字・title・aria-label は板（`LaneBoard`）が決める。ここが決めるのは
 * 行き先の名前とリンクの宛先だけである。
 *
 * **案件は画面が受け取った番号をそのまま渡す**（`view-links/common.ts` の docblock と同じ判断）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 看板のカードの「詳細」ボタンの行き先（ステージの図・業務フローの図・業務詳細・業務ルール・画面詳細）と名前を、案件を引き継いで組む。
 * @graph-connects pjLinkSearch [calls] 案件を検索条件へ載せる
 */
import { Link } from '@tanstack/react-router';

import type { LaneCardDetail } from '../components/spec/LaneBoard.js';
import { pjLinkSearch } from './pj-link.js';
import {
  BUSINESS_DETAIL_ROUTE,
  FLOW_ROUTE,
  PJ_FLOW_ROUTE,
  RULE_ROUTE,
  SCREEN_DETAIL_ROUTE,
  STAGE_ROUTE,
} from './route-paths.js';
import { pjLinkValue } from './view-links/common.js';

/** 文言を引く関数。 @graph-connects none */
type Translate = (key: string) => string;

/**
 * ステージのカードの「詳細」— ステージの図。案件はステージの図の検索条件（`?pj=`）へ載せる。
 *
 * @param stageId ステージの id
 * @param pjNumber 重ねる案件番号。master を見ているなら undefined
 * @param t 文言
 * @returns 「詳細」ボタンの中身
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージのカードの「詳細」の行き先を、案件を引き継いだステージの図にする。
 * @graph-connects pjLinkSearch [calls] 案件を検索条件へ載せる
 */
export function stageCardDetail(
  stageId: number,
  pjNumber: number | undefined,
  t: Translate,
): LaneCardDetail {
  return {
    destination: t('overview.openStageDiagram'),
    link: (anchor) => (
      <Link
        to={STAGE_ROUTE}
        params={{ stageId: String(stageId) }}
        {...pjLinkSearch(pjLinkValue(pjNumber))}
        {...anchor}
      />
    ),
  };
}

/**
 * 業務フローのカードの「詳細」— 業務フローの図。
 *
 * **案件モードでは行き先そのものが替わる。** 業務フローの図は検索条件の案件を読まず、案件つきの
 * 姿は案件をパスの親に置く別のルート（`/pjs/$pjNumber/flows/$flowId`）が受け持つ（`FlowLink` と
 * 同じ理由）。
 *
 * @param flowId 業務フローの id
 * @param pjNumber 重ねる案件番号。master を見ているなら undefined
 * @param t 文言
 * @returns 「詳細」ボタンの中身
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローのカードの「詳細」の行き先を、master なら業務フローの図、案件なら案件から見た業務フローの図にする。
 * @graph-connects none
 */
export function flowCardDetail(
  flowId: number,
  pjNumber: number | undefined,
  t: Translate,
): LaneCardDetail {
  const id = String(flowId);
  return {
    destination: t('overview.openFlowDiagram'),
    link: (anchor) =>
      pjNumber === undefined ? (
        <Link to={FLOW_ROUTE} params={{ flowId: id }} {...anchor} />
      ) : (
        <Link to={PJ_FLOW_ROUTE} params={{ pjNumber: String(pjNumber), flowId: id }} {...anchor} />
      ),
  };
}

/**
 * 業務のカードの「詳細」— 業務詳細（データの流れの図）。
 *
 * master も案件モードも同じ業務詳細へ送る。業務ルールの見方を業務詳細の図 1 つに寄せ、業務ロジック
 * （一覧）を消したためである（Dhik 2026-09-30）。案件は業務詳細の検索条件（`?pj=`）へ載せる。
 *
 * @param businessId 業務の id
 * @param pjNumber 重ねる案件番号。master を見ているなら undefined
 * @param t 文言
 * @returns 「詳細」ボタンの中身
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務のカードの「詳細」の行き先を、案件を引き継いだ業務詳細にする。
 * @graph-connects pjLinkSearch [calls] 案件を検索条件へ載せる
 */
export function businessCardDetail(
  businessId: number,
  pjNumber: number | undefined,
  t: Translate,
): LaneCardDetail {
  const params = { businessId: String(businessId) };
  return {
    destination: t('businessLogic.openBusinessDetail'),
    link: (anchor) => (
      <Link
        to={BUSINESS_DETAIL_ROUTE}
        params={params}
        {...pjLinkSearch(pjLinkValue(pjNumber))}
        {...anchor}
      />
    ),
  };
}

/**
 * 業務ルールのカードの「詳細」— 業務ルールの画面（`/rules/$ruleId`）。
 *
 * 業務ルールは業務から独立しているので（引継書 §2）、持ち主の業務を決めずに開ける。カードそのものは
 * 押せず、業務ルールの画面へはこのボタンでだけ行く。
 *
 * @param ruleId 業務ルールの id
 * @param pjNumber 重ねる案件番号。master を見ているなら undefined
 * @param t 文言
 * @returns 「詳細」ボタンの中身
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールのカードの「詳細」の行き先を、案件を引き継いだ業務ルールの画面にする。
 * @graph-connects pjLinkSearch [calls] 案件を検索条件へ載せる
 */
export function ruleCardDetail(
  ruleId: number,
  pjNumber: number | undefined,
  t: Translate,
): LaneCardDetail {
  return {
    destination: t('overview.openRule'),
    link: (anchor) => (
      <Link
        to={RULE_ROUTE}
        params={{ ruleId: String(ruleId) }}
        {...pjLinkSearch(pjLinkValue(pjNumber))}
        {...anchor}
      />
    ),
  };
}

/**
 * 画面のカードの「詳細」— 画面詳細（`/screens/$screenId`）。案件は検索条件（`?pj=`）へ載せる。
 *
 * 画面のカードは右に絞る先が無いので押せない。画面詳細へはこのボタンでだけ行く（業務ルールのカードと
 * 同じ。案件の変更一覧・画面一覧のどちらも）。
 *
 * @param screenId 画面の id
 * @param pjNumber 重ねる案件番号
 * @param t 文言
 * @returns 「詳細」ボタンの中身
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面のカードの「詳細」の行き先を、案件を引き継いだ画面詳細にする。
 * @graph-connects pjLinkSearch [calls] 案件を検索条件へ載せる
 */
export function screenCardDetail(
  screenId: number,
  pjNumber: number | undefined,
  t: Translate,
): LaneCardDetail {
  return {
    destination: t('pjLanes.openScreen'),
    link: (anchor) => (
      <Link
        to={SCREEN_DETAIL_ROUTE}
        params={{ screenId: String(screenId) }}
        {...pjLinkSearch(pjLinkValue(pjNumber))}
        {...anchor}
      />
    ),
  };
}
